import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { onSignOut } from '../lib/signOutReset';
import { RelationshipCategory, RELATIONSHIP_CATEGORIES } from '../constants/relationshipTypes';
import { BondRankName, getBondRankFromXP } from '../constants/bondRanks';
import { PointsDistribution, calculateActivityXP } from '../utils/socialActivityXP';
import { deriveNetworkStatus, type NetworkStatus } from '../utils/networkStatus';
import { distributeByType } from '../constants/interactionAttribution';
import { getActivityType } from '../constants/activityTypes';
import { useSocialParametersStore } from './socialParametersStore';
import { useSocialStore } from './socialStore';

export interface SocialBond {
  id: string;
  user_id: string;
  contact_name: string;
  contact_email: string | null;
  contact_user_id: string | null;
  relationship_type: string;
  relationship_category: RelationshipCategory;
  bond_xp: number;
  bond_level: number;
  bond_rank: BondRankName;
  profile_image_url: string | null;
  notes: string | null;
  last_interaction_at: string | null;
  /** Stay-in-touch cadence in days (SO8); null = no cadence. */
  contact_cadence_days: number | null;
  created_at: string;
  updated_at: string;
}

export interface SocialBondActivity {
  id: string;
  user_id: string;
  bond_id: string;
  activity_name: string;
  activity_type: string;
  duration_minutes: number;
  points_available: number;
  points_distributed: boolean;
  xp_earned_user: number;
  xp_earned_domain: number;
  xp_earned_bond: number;
  location: string | null;
  notes: string | null;
  completed_at: string;
  created_at: string;
}

export interface SocialActivityAssessment {
  id: string;
  user_id: string;
  activity_id: string;
  assessor_type: 'self' | 'external';
  points_available: number;
  points_distributed: PointsDistribution;
  xp_per_point: number;
  total_xp_earned: number;
  created_at: string;
}

export interface ExternalAssessmentToken {
  id: string;
  token: string;
  user_id: string;
  activity_id: string;
  bond_id: string;
  contact_name: string;
  contact_email: string | null;
  points_available: number;
  expires_at: string;
  is_used: boolean;
  used_at: string | null;
  created_at: string;
}

export interface NetworkRequest {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  responded_at: string | null;
  /** The OTHER party's public profile (two-step fetch — FKs are auth.users,
   *  so PostgREST embeds are impossible, the R2 lesson). */
  profile?: {
    id: string;
    name: string;
    handle: string | null;
    avatar: string | null;
  };
}

interface CreateBondInput {
  contact_name: string;
  contact_email?: string;
  relationship_type: string;
  relationship_category: RelationshipCategory;
  profile_image_url?: string;
  notes?: string;
  contact_cadence_days?: number | null;
}

interface LogActivityInput {
  bond_id: string;
  activity_name: string;
  activity_type: string;
  duration_minutes: number;
  location?: string;
  notes?: string;
  completed_at?: string;
}

interface SocialBondsState {
  bonds: SocialBond[];
  activities: SocialBondActivity[];
  assessments: SocialActivityAssessment[];
  pendingAssessmentTokens: ExternalAssessmentToken[];
  networkRequests: NetworkRequest[];
  isLoading: boolean;
  error: string | null;

  fetchBonds: () => Promise<void>;
  fetchNetworkRequests: () => Promise<void>;
  sendNetworkRequest: (userId: string) => Promise<boolean>;
  respondNetworkRequest: (requestId: string, accept: boolean) => Promise<boolean>;
  /** Withdraw my pending request (or clear a declined one to re-request). */
  cancelNetworkRequest: (requestId: string) => Promise<void>;
  getNetworkStatus: (myId: string, otherUserId: string) => NetworkStatus;
  fetchActivities: (bondId?: string) => Promise<void>;
  createBond: (input: CreateBondInput) => Promise<SocialBond | null>;
  updateBond: (id: string, updates: Partial<CreateBondInput>) => Promise<void>;
  deleteBond: (id: string) => Promise<void>;
  logActivity: (input: LogActivityInput) => Promise<SocialBondActivity | null>;
  submitAssessment: (
    activityId: string,
    pointsDistribution: PointsDistribution,
    socialDomainLevel?: number
  ) => Promise<void>;
  /** SO5 — two-tap log: records the interaction and auto-distributes its points
   *  across capacities by the interaction type (logActivity + submitAssessment). */
  quickLogInteraction: (
    bondId: string,
    activityType: string,
    durationMinutes?: number
  ) => Promise<void>;
  generateExternalAssessmentLink: (activityId: string, bondId: string) => Promise<string | null>;
  getBondsByCategory: (category: RelationshipCategory) => SocialBond[];
  getBondsByRank: (rank: BondRankName) => SocialBond[];
  getBond: (id: string) => SocialBond | undefined;
  getRecentActivities: (limit?: number) => SocialBondActivity[];
  getNetworkStats: () => {
    totalBonds: number;
    byCategory: Record<RelationshipCategory, number>;
    byRank: Record<string, number>;
    averageBondLevel: number;
  };
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

/** An action's failure — shown only while the account it ran for is still signed in. */
function failed(at: number, error: unknown) {
  if (at === epoch) useSocialBondsStore.setState({ error: (error as Error).message });
}

export const useSocialBondsStore = create<SocialBondsState>((set, get) => ({
  bonds: [],
  activities: [],
  assessments: [],
  pendingAssessmentTokens: [],
  networkRequests: [],
  isLoading: false,
  error: null,

  fetchNetworkRequests: async () => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      // RLS already scopes to rows I'm a party to
      const { data, error } = await supabase
        .from('network_requests')
        .select('*')
        .order('created_at', { ascending: false });

      // PGRST205/42P01 = table not migrated yet — stay quietly empty
      if (error?.code === 'PGRST205' || error?.code === '42P01') return;
      if (error) throw error;
      const rows = (data || []) as NetworkRequest[];

      // hydrate the OTHER party's profile (two-step; FK is auth.users)
      const otherIds = [...new Set(rows.map(r => (r.requester_id === user.id ? r.addressee_id : r.requester_id)))];
      let profiles = new Map<string, NetworkRequest['profile']>();
      if (otherIds.length > 0) {
        const { data: profileRows } = await supabase
          .from('user_profiles')
          .select('id, name, handle, avatar_url')
          .in('id', otherIds);
        profiles = new Map(
          (profileRows || []).map(p => [
            p.id,
            { id: p.id, name: p.name || 'Unknown User', handle: p.handle || null, avatar: p.avatar_url || null },
          ])
        );
      }

      // Signed out during either read: none of it is the next account's.
      if (at !== epoch) return;
      set({
        networkRequests: rows.map(r => ({
          ...r,
          profile: profiles.get(r.requester_id === user.id ? r.addressee_id : r.requester_id),
        })),
      });
    } catch (error) {
      // table missing pre-migration — leave state empty, surfaces hide themselves
      console.error('Error fetching network requests:', error);
    }
  },

  sendNetworkRequest: async (userId) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user || user.id === userId) return false;

      const { error } = await supabase
        .from('network_requests')
        .insert({ requester_id: user.id, addressee_id: userId });

      if (error) throw error;
      await get().fetchNetworkRequests();
      return true;
    } catch (error) {
      failed(at, error);
      return false;
    }
  },

  respondNetworkRequest: async (requestId, accept) => {
    const at = epoch;
    try {
      const { error } = await supabase.rpc('respond_network_request', {
        p_request_id: requestId,
        p_accept: accept,
      });
      if (error) throw error;

      await Promise.all([get().fetchNetworkRequests(), accept ? get().fetchBonds() : Promise.resolve()]);
      return true;
    } catch (error) {
      failed(at, error);
      return false;
    }
  },

  cancelNetworkRequest: async (requestId) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('network_requests')
        .delete()
        .eq('id', requestId);
      if (error) throw error;
      set(state => ({ networkRequests: state.networkRequests.filter(r => r.id !== requestId) }));
    } catch (error) {
      failed(at, error);
    }
  },

  getNetworkStatus: (myId, otherUserId) =>
    deriveNetworkStatus(myId, otherUserId, get().networkRequests),

  fetchBonds: async () => {
    const at = epoch;
    set({ isLoading: true, error: null });
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) {
        set({ isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('social_bonds')
        .select('*')
        .eq('user_id', user.id)
        .order('last_interaction_at', { ascending: false, nullsFirst: false });
      // Signed out while it read: the next account must not get this one's bonds.
      if (at !== epoch) return;

      if (error) throw error;

      set({ bonds: data as SocialBond[], isLoading: false });
    } catch (error) {
      if (at === epoch) set({ error: (error as Error).message, isLoading: false });
    }
  },

  fetchActivities: async (bondId) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      let query = supabase
        .from('social_bond_activities')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (bondId) {
        query = query.eq('bond_id', bondId);
      }

      const { data, error } = await query.limit(100);
      if (at !== epoch) return; // signed out while it read

      if (error) throw error;

      set({ activities: data as SocialBondActivity[] });
    } catch (error) {
      failed(at, error);
    }
  },

  createBond: async (input) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;

      const { data, error } = await supabase
        .from('social_bonds')
        .insert({
          user_id: user.id,
          contact_name: input.contact_name,
          contact_email: input.contact_email || null,
          relationship_type: input.relationship_type,
          relationship_category: input.relationship_category,
          profile_image_url: input.profile_image_url || null,
          notes: input.notes || null,
          bond_xp: 0,
          bond_level: 0,
          bond_rank: 'Stranger'
        })
        .select()
        .single();

      if (error) throw error;

      await get().fetchBonds();
      return data as SocialBond;
    } catch (error) {
      failed(at, error);
      return null;
    }
  },

  updateBond: async (id, updates) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('social_bonds')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      await get().fetchBonds();
    } catch (error) {
      failed(at, error);
    }
  },

  deleteBond: async (id) => {
    const at = epoch;
    try {
      const { error } = await supabase
        .from('social_bonds')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await get().fetchBonds();
    } catch (error) {
      failed(at, error);
    }
  },

  logActivity: async (input) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;

      const pointsAvailable = Math.max(1, Math.min(12, Math.round((input.duration_minutes / 60) * 3)));

      const { data, error } = await supabase
        .from('social_bond_activities')
        .insert({
          user_id: user.id,
          bond_id: input.bond_id,
          activity_name: input.activity_name,
          activity_type: input.activity_type,
          duration_minutes: input.duration_minutes,
          points_available: pointsAvailable,
          points_distributed: false,
          location: input.location || null,
          notes: input.notes || null,
          completed_at: input.completed_at || new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      await supabase
        .from('social_bonds')
        .update({ last_interaction_at: input.completed_at || new Date().toISOString() })
        .eq('id', input.bond_id);

      await get().fetchBonds();
      await get().fetchActivities();

      return data as SocialBondActivity;
    } catch (error) {
      failed(at, error);
      return null;
    }
  },

  submitAssessment: async (activityId, pointsDistribution, socialDomainLevel = 1) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;

      const activity = get().activities.find(a => a.id === activityId);
      if (!activity) throw new Error('Activity not found');

      const xpBreakdown = calculateActivityXP(
        activity.duration_minutes,
        pointsDistribution,
        socialDomainLevel
      );

      const { error: assessmentError } = await supabase
        .from('social_activity_assessments')
        .insert({
          user_id: user.id,
          activity_id: activityId,
          assessor_type: 'self',
          points_available: activity.points_available,
          points_distributed: pointsDistribution,
          xp_per_point: Math.round(xpBreakdown.totalParameterXP / Object.values(pointsDistribution).reduce((a, b) => a + b, 0)),
          total_xp_earned: xpBreakdown.totalParameterXP
        });

      if (assessmentError) throw assessmentError;

      const { error: activityUpdateError } = await supabase
        .from('social_bond_activities')
        .update({
          points_distributed: true,
          xp_earned_user: xpBreakdown.userXP,
          xp_earned_domain: xpBreakdown.domainXP,
          xp_earned_bond: xpBreakdown.bondXP
        })
        .eq('id', activityId);

      if (activityUpdateError) throw activityUpdateError;

      const bond = get().bonds.find(b => b.id === activity.bond_id);
      if (bond) {
        const newBondXP = bond.bond_xp + xpBreakdown.bondXP;
        const newRank = getBondRankFromXP(newBondXP);

        await supabase
          .from('social_bonds')
          .update({
            bond_xp: newBondXP,
            bond_level: newRank.level,
            bond_rank: newRank.name
          })
          .eq('id', bond.id);
      }

      // ⚠️⚠️ Signed out meanwhile: the Capacity XP is asked for the account signed in
      // WHEN it is called, and finds each parameter by NAME — the next account's.
      if (at !== epoch) return;
      await useSocialParametersStore.getState().distributePoints(
        pointsDistribution,
        'activity',
        activityId,
        socialDomainLevel
      );

      const totalDomainXP = xpBreakdown.domainXP + xpBreakdown.userXP;

      const { data: existingStats } = await supabase
        .from('domain_stats')
        .select('points, social_actions_count')
        .eq('user_id', user.id)
        .eq('domain_id', 'social')
        .maybeSingle();

      if (existingStats) {
        await supabase
          .from('domain_stats')
          .update({
            points: existingStats.points + totalDomainXP,
            social_actions_count: (existingStats.social_actions_count || 0) + 1,
            last_social_activity_date: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('domain_id', 'social');
      } else {
        await supabase
          .from('domain_stats')
          .insert({
            user_id: user.id,
            domain_id: 'social',
            points: totalDomainXP,
            level: 1,
            social_actions_count: 1,
            last_social_activity_date: new Date().toISOString()
          });
      }

      await useSocialStore.getState().refreshSocialStats();

      await get().fetchBonds();
      await get().fetchActivities();
    } catch (error) {
      failed(at, error);
    }
  },

  quickLogInteraction: async (bondId, activityType, durationMinutes) => {
    const at = epoch;
    const duration = durationMinutes ?? getActivityType(activityType)?.defaultDuration ?? 60;
    const activity = await get().logActivity({
      bond_id: bondId,
      activity_name: activityType,
      activity_type: activityType,
      duration_minutes: duration,
      completed_at: new Date().toISOString(),
    });
    // Signed out while it logged: the assessment would run for the account signed in now.
    if (!activity || at !== epoch) return;

    const distribution = distributeByType(activityType, activity.points_available);
    const socialDomainLevel = useSocialStore.getState().socialStats?.level ?? 1;
    await get().submitAssessment(activity.id, distribution, socialDomainLevel);
  },

  generateExternalAssessmentLink: async (activityId, bondId) => {
    const at = epoch;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return null;

      const activity = get().activities.find(a => a.id === activityId);
      const bond = get().bonds.find(b => b.id === bondId);

      if (!activity || !bond) throw new Error('Activity or bond not found');

      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 48);

      const { data, error } = await supabase
        .from('external_assessment_tokens')
        .insert({
          user_id: user.id,
          activity_id: activityId,
          bond_id: bondId,
          contact_name: bond.contact_name,
          contact_email: bond.contact_email,
          points_available: activity.points_available,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      const baseUrl = window.location.origin;
      return `${baseUrl}/assess/${data.token}`;
    } catch (error) {
      failed(at, error);
      return null;
    }
  },

  getBondsByCategory: (category) => {
    return get().bonds.filter(b => b.relationship_category === category);
  },

  getBondsByRank: (rank) => {
    return get().bonds.filter(b => b.bond_rank === rank);
  },

  getBond: (id) => {
    return get().bonds.find(b => b.id === id);
  },

  getRecentActivities: (limit = 10) => {
    return get().activities.slice(0, limit);
  },

  getNetworkStats: () => {
    const bonds = get().bonds;

    const byCategory = Object.fromEntries(
      RELATIONSHIP_CATEGORIES.map(c => [c, 0])
    ) as Record<RelationshipCategory, number>;

    const byRank: Record<string, number> = {};

    let totalLevel = 0;

    bonds.forEach(bond => {
      // tolerate any legacy/unknown category (pre-migration rows)
      byCategory[bond.relationship_category] = (byCategory[bond.relationship_category] || 0) + 1;
      byRank[bond.bond_rank] = (byRank[bond.bond_rank] || 0) + 1;
      totalLevel += bond.bond_level;
    });

    return {
      totalBonds: bonds.length,
      byCategory,
      byRank,
      averageBondLevel: bonds.length > 0 ? Math.round(totalLevel / bonds.length) : 0
    };
  },

  /** Back to empty — run on sign-out (lib/signOutReset). No `persist`, but a sign-out does not reload the page. */
  clearStore: () => {
    epoch += 1;
    set({ bonds: [], activities: [], assessments: [], pendingAssessmentTokens: [], networkRequests: [], isLoading: false, error: null });
  },
}));

onSignOut(() => useSocialBondsStore.getState().clearStore());
